// /////////////////////////////////////////////////////////////
// [ファイル] SymbolMetaPanel.tsx
// [概要] ソース品質管理プラットフォームの右ペイン。
//        選択シンボルの品質メタ情報 (レビュー状態 / 担当 / タグ / 課題 /
//        AI コメント) を表示・編集する。フェーズ1 (MVP) の編集は
//        useSourceQuality のローカル state に反映される。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import {
  SourceSymbol,
  ReviewStatus,
  IssueSeverity,
  REVIEW_STATUS_LABEL,
  REVIEW_STATUS_ORDER,
  ISSUE_SEVERITY_LABEL,
} from '../../types/sourceQuality';
import { REVIEW_STATUS_COLOR, ISSUE_SEVERITY_COLOR } from './statusVisuals';
import { SAMPLE_ASSIGNEES } from '../../data/sourceQualitySample';
import { NewIssueInput } from '../../hooks/useSourceQuality';
import { useConfirm } from '../../providers/ConfirmProvider';
import { useNotification } from '../../providers/NotificationProvider';
import { STANDARD_CTA_BUTTON_SX } from '../../constants/buttonStyles';

const UNASSIGNED = '（未割当）';

interface SymbolMetaPanelProps {
  symbol: SourceSymbol;
  onChangeStatus: (status: ReviewStatus) => void;
  onChangeAssignee: (assignee: string | null) => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  onAddIssue: (input: NewIssueInput) => void;
  onRemoveIssue: (id: string) => void;
  onToggleIssue: (id: string) => void;
}

const SectionLabel: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <Typography
    variant="caption"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      fontWeight: 700,
      color: 'text.secondary',
      mb: 0.75,
    }}
  >
    {icon}
    {children}
  </Typography>
);

export default function SymbolMetaPanel({
  symbol,
  onChangeStatus,
  onChangeAssignee,
  onAddTag,
  onRemoveTag,
  onAddIssue,
  onRemoveIssue,
  onToggleIssue,
}: SymbolMetaPanelProps) {
  const confirm = useConfirm();
  const notify = useNotification();

  const [newTag, setNewTag] = useState('');
  const [issueTitle, setIssueTitle] = useState('');
  const [issueSeverity, setIssueSeverity] = useState<IssueSeverity>('medium');
  const [issueBody, setIssueBody] = useState('');

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    onAddTag(newTag);
    setNewTag('');
  };

  const handleAddIssue = () => {
    if (!issueTitle.trim()) {
      notify.warning('課題のタイトルを入力してください');
      return;
    }
    onAddIssue({ title: issueTitle, body: issueBody, severity: issueSeverity });
    setIssueTitle('');
    setIssueBody('');
    setIssueSeverity('medium');
    notify.success('課題を追加しました');
  };

  const handleRemoveIssue = async (id: string, title: string) => {
    const ok = await confirm({
      title: '課題の削除',
      message: `「${title}」を削除しますか？`,
      destructive: true,
    });
    if (!ok) return;
    onRemoveIssue(id);
    notify.success('課題を削除しました');
  };

  const handleReanalyze = () => {
    notify.info('AI 再分析はフェーズ2で実装予定です');
  };

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      {/* レビュー状態 */}
      <SectionLabel icon={<Icons.FactCheck sx={{ fontSize: 15 }} />}>レビュー状態</SectionLabel>
      <Select
        size="small"
        fullWidth
        value={symbol.review_status}
        onChange={(e) => onChangeStatus(e.target.value as ReviewStatus)}
        sx={{ fontSize: '0.8rem', mb: 2 }}
      >
        {REVIEW_STATUS_ORDER.map((st) => (
          <MenuItem key={st} value={st} sx={{ fontSize: '0.8rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: REVIEW_STATUS_COLOR[st] }} />
              {REVIEW_STATUS_LABEL[st]}
            </Box>
          </MenuItem>
        ))}
      </Select>

      {/* 担当 */}
      <SectionLabel icon={<Icons.Person sx={{ fontSize: 15 }} />}>担当</SectionLabel>
      <Select
        size="small"
        fullWidth
        value={symbol.assignee ?? UNASSIGNED}
        onChange={(e) =>
          onChangeAssignee(e.target.value === UNASSIGNED ? null : (e.target.value as string))
        }
        sx={{ fontSize: '0.8rem', mb: 2 }}
      >
        {SAMPLE_ASSIGNEES.map((a) => (
          <MenuItem key={a} value={a} sx={{ fontSize: '0.8rem' }}>
            {a}
          </MenuItem>
        ))}
      </Select>

      {/* タグ */}
      <SectionLabel icon={<Icons.Category sx={{ fontSize: 15 }} />}>タグ</SectionLabel>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {symbol.tags.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            タグなし
          </Typography>
        )}
        {symbol.tags.map((t) => (
          <Chip
            key={t}
            label={t}
            size="small"
            onDelete={() => onRemoveTag(t)}
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
        <TextField
          size="small"
          placeholder="タグを追加"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddTag();
            }
          }}
          sx={{ flexGrow: 1, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.5 } }}
        />
        <IconButton size="small" onClick={handleAddTag} aria-label="タグを追加">
          <Icons.Add fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* 課題 */}
      <SectionLabel icon={<Icons.WarningAmber sx={{ fontSize: 15 }} />}>
        課題 ({symbol.issues.filter((i) => i.status === 'open').length})
      </SectionLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
        {symbol.issues.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            課題はありません
          </Typography>
        )}
        {symbol.issues.map((issue) => {
          const resolved = issue.status === 'resolved';
          return (
            <Box
              key={issue.id}
              sx={{
                p: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: resolved ? 'action.hover' : 'transparent',
                opacity: resolved ? 0.6 : 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: ISSUE_SEVERITY_COLOR[issue.severity],
                    mt: 0.6,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      textDecoration: resolved ? 'line-through' : 'none',
                    }}
                  >
                    {issue.title}
                  </Typography>
                  {issue.body && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {issue.body}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                    重要度 {ISSUE_SEVERITY_LABEL[issue.severity]}
                    {issue.created_at ? ` ・ ${issue.created_at}` : ''}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => onToggleIssue(issue.id)}
                  aria-label={resolved ? '未対応に戻す' : '解決にする'}
                  title={resolved ? '未対応に戻す' : '解決にする'}
                >
                  <Icons.Check fontSize="small" color={resolved ? 'success' : 'inherit'} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveIssue(issue.id, issue.title)}
                  aria-label="課題を削除"
                >
                  <Icons.Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* 課題 追加フォーム */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
        <TextField
          size="small"
          placeholder="課題のタイトル"
          value={issueTitle}
          onChange={(e) => setIssueTitle(e.target.value)}
          sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.5 } }}
        />
        <TextField
          size="small"
          placeholder="詳細 (任意)"
          value={issueBody}
          onChange={(e) => setIssueBody(e.target.value)}
          multiline
          minRows={1}
          sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Select
            size="small"
            value={issueSeverity}
            onChange={(e) => setIssueSeverity(e.target.value as IssueSeverity)}
            sx={{ fontSize: '0.78rem', minWidth: 90 }}
          >
            {(['high', 'medium', 'low'] as IssueSeverity[]).map((sv) => (
              <MenuItem key={sv} value={sv} sx={{ fontSize: '0.78rem' }}>
                重要度 {ISSUE_SEVERITY_LABEL[sv]}
              </MenuItem>
            ))}
          </Select>
          <Button
            onClick={handleAddIssue}
            startIcon={<Icons.Add />}
            sx={{ ...STANDARD_CTA_BUTTON_SX, flexGrow: 1 }}
          >
            課題を追加
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* AI コメント */}
      <SectionLabel icon={<Icons.ChatBubbleOutline sx={{ fontSize: 15 }} />}>AI コメント</SectionLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
        {symbol.ai_comments.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            AI コメントはありません
          </Typography>
        )}
        {symbol.ai_comments.map((c) => (
          <Box
            key={c.id}
            sx={{ p: 1, borderRadius: 1, bgcolor: '#f3f6fb', border: '1px solid #e3eaf3' }}
          >
            <Typography variant="caption" sx={{ color: '#5c7c9e', fontWeight: 700, display: 'block' }}>
              {c.type}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
              {c.body}
            </Typography>
          </Box>
        ))}
      </Box>
      <Button
        fullWidth
        onClick={handleReanalyze}
        startIcon={<Icons.AutoAwesome />}
        sx={STANDARD_CTA_BUTTON_SX}
      >
        このシンボルを AI 再分析
      </Button>
    </Box>
  );
}
